import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckIcon, IndeterminateIcon } from '../../Icons';
import { CheckboxLabelGroup } from './CheckBoxLabelGroup';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { styled } from '../../system';
export const Checkbox = forwardRef(({ label, helpText, onCheckedChange, size = 'sm', ...props }, ref) => {
    var _a;
    const [checked, setChecked] = useState((_a = props.checked) !== null && _a !== void 0 ? _a : props.defaultChecked);
    const hasText = useMemo(() => Boolean(label !== null && label !== void 0 ? label : helpText), [label, helpText]);
    const handleCheckedChange = useCallback((checked) => {
        setChecked(checked);
        if (typeof onCheckedChange === 'function') {
            onCheckedChange(checked);
        }
    }, [onCheckedChange]);
    return (_jsx(CheckboxLabelGroup, { helpText: helpText, label: label, size: size, children: _jsx(StyledCheckbox, { ...props, ref: ref, css: {
                mt: hasText ? '$0dot5' : 0,
                ...props.css
            }, size: size, onCheckedChange: handleCheckedChange, children: _jsxs(StyledIndicator, { children: [checked === true && (_jsx(CheckIcon, { width: size === 'sm' ? 10 : 12, height: size === 'sm' ? 8 : 10 })), checked === 'indeterminate' && (_jsx(IndeterminateIcon, { width: size === 'sm' ? 10 : 12, height: size === 'sm' ? 12 : 2 }))] }) }) }));
});
const StyledCheckbox = styled(CheckboxPrimitive.Root, {
    p: 0,
    cursor: 'pointer',
    position: 'relative',
    color: '$checkboxIndicator',
    bgColor: '$checkboxBg',
    border: '1px solid $checkboxBorder',
    rounded: '$base',
    flexCenter: true,
    lineHeight: '$none',
    '&:focus:not([data-disabled])': {
        color: '$checkboxIndicatorFocus',
        bgColor: '$checkboxBgFocus',
        borderColor: '$checkboxBorderFocus',
        shadow: '$focusRingPrimary'
    },
    '&:active:not([data-disabled])': {
        color: '$checkboxIndicatorActive',
        bgColor: '$checkboxBgActive',
        borderColor: '$checkboxBorderActive',
        shadow: '$focusRingPrimary'
    },
    '&[data-state="checked"]:not([data-disabled]), &[data-state="indeterminate"]:not([data-disabled])': {
        color: '$checkboxIndicatorChecked',
        bgColor: '$checkboxBgChecked',
        borderColor: '$checkboxBorderChecked'
    },
    '@hover': {
        '&:hover:not([data-disabled])': {
            color: '$checkboxIndicatorHover',
            bgColor: '$checkboxBgHover',
            borderColor: '$checkboxBorderHover'
        }
    },
    '&[data-disabled]': {
        color: '$checkboxIndicatorDisabled',
        bgColor: '$checkboxBgDisabled',
        borderColor: '$checkboxBorderDisabled',
        opacity: 0.8,
        cursor: 'not-allowed'
    },
    variants: {
        size: {
            sm: {
                minMaxSize: '$4'
            },
            md: {
                minMaxSize: '$5'
            }
        },
        shape: {
            square: {},
            circle: {
                rounded: '$full',
                '&[data-state="checked"]:not([data-disabled]), &[data-state="indeterminate"]:not([data-disabled])': {
                    color: '$checkboxCircleIndicatorChecked',
                    bgColor: '$checkboxCircleBgChecked',
                    borderColor: '$checkboxCircleBorderChecked'
                }
            }
        }
    },
    defaultVariants: {
        size: 'sm',
        shape: 'square'
    }
});
const StyledIndicator = styled(CheckboxPrimitive.Indicator, {
    w: '$full',
    h: '$full',
    flexCenter: true,
    lineHeight: '$none',
    position: 'absolute',
    left: 0,
    top: 0
});
Checkbox.displayName = 'Checkbox';
